<template>
  <div class="breakdown-chart">
    <div class="breakdown-chart__header">
      <h3 class="breakdown-chart__title">{{ title }}</h3>
    </div>

    <div class="breakdown-chart__metric-tabs">
      <button
        v-for="m in metrics"
        :key="m.key"
        type="button"
        :class="['breakdown-chart__pill', { 'is-active': activeMetric === m.key }]"
        @click="activeMetric = m.key"
      >{{ m.label }}</button>
    </div>

    <div class="breakdown-chart__main">
      <div class="breakdown-chart__chart-area">
        <!-- ドーナツチャート (表示数/クリック数) -->
        <div v-if="!isRatio" class="breakdown-chart__donut-wrap">
          <svg :viewBox="`0 0 ${svgSize} ${svgSize}`" class="breakdown-chart__svg">
            <path
              v-for="seg in donutSegments"
              :key="seg.name"
              :d="seg.path"
              :fill="seg.fill"
              stroke="#ffffff"
              stroke-width="2"
              class="breakdown-chart__donut-segment"
              @mousemove="onHover($event, seg)"
              @mouseleave="onLeave"
            />
            <template v-for="seg in donutSegments" :key="'l-' + seg.name">
              <text
                v-if="seg.percent >= 5"
                :x="seg.labelX"
                :y="seg.labelY"
                text-anchor="middle"
                dominant-baseline="central"
                class="breakdown-chart__segment-label"
              >{{ Math.round(seg.percent) }}%</text>
            </template>
          </svg>
          <div class="breakdown-chart__donut-center">
            <div class="breakdown-chart__donut-center-label">合計</div>
            <div class="breakdown-chart__donut-center-value">{{ formatValue(totalValue, 0) }}</div>
            <div class="breakdown-chart__donut-center-sub">{{ metric.label }}</div>
          </div>
        </div>

        <!-- 横棒グラフ (CTR) -->
        <div v-else class="breakdown-chart__bar-wrap">
          <svg :viewBox="`0 0 ${barWidth} ${barHeight}`" class="breakdown-chart__svg" preserveAspectRatio="xMidYMid meet">
            <g>
              <line
                v-for="g in gridLines"
                :key="'g-' + g.x"
                :x1="g.x"
                :x2="g.x"
                :y1="0"
                :y2="barInnerHeight"
                stroke="#e0edf0"
                stroke-dasharray="2 4"
              />
              <text
                v-for="g in gridLines"
                :key="'gt-' + g.x"
                :x="g.x"
                :y="barInnerHeight + 14"
                text-anchor="middle"
                class="breakdown-chart__axis-text"
              >{{ g.label }}</text>
            </g>
            <g v-for="bar in barItems" :key="bar.name">
              <text
                :x="barLabelWidth - 8"
                :y="bar.y + bar.height / 2"
                text-anchor="end"
                dominant-baseline="central"
                class="breakdown-chart__bar-name"
              >{{ bar.name }}</text>
              <rect
                :x="barLabelWidth"
                :y="bar.y"
                :width="bar.width"
                :height="bar.height"
                :fill="bar.fill"
                rx="2"
                ry="2"
                @mousemove="onHover($event, bar)"
                @mouseleave="onLeave"
              />
              <text
                :x="barLabelWidth + bar.width + 6"
                :y="bar.y + bar.height / 2"
                dominant-baseline="central"
                class="breakdown-chart__bar-value"
              >{{ formatValue(bar.rawValue, 2) }}%</text>
            </g>
          </svg>
        </div>
      </div>

      <div class="breakdown-chart__legend">
        <div class="breakdown-chart__legend-title">内訳</div>
        <div class="breakdown-chart__legend-list">
          <div
            v-for="(d, i) in data"
            :key="d[nameKey]"
            class="breakdown-chart__legend-item"
          >
            <span class="breakdown-chart__legend-swatch" :style="{ background: colorFor(d[nameKey], i) }"></span>
            <span class="breakdown-chart__legend-label">{{ d[nameKey] }}</span>
            <span class="breakdown-chart__legend-value">
              {{ formatValue(metricValue(d), metric.unit === '%' ? 2 : 0) }}{{ metric.unit }}
              <span v-if="legendPercent(d) !== null" class="breakdown-chart__legend-percent">({{ legendPercent(d) }}%)</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="tooltip.visible"
      class="breakdown-chart__tooltip"
      :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
    >
      <div class="breakdown-chart__tooltip-name" :style="{ color: tooltip.fill }">{{ tooltip.name }}</div>
      <div class="breakdown-chart__tooltip-row">
        <span>{{ metric.label }}</span>
        <span class="breakdown-chart__tooltip-value">{{ formatValue(tooltip.value, metric.unit === '%' ? 2 : 0) }}{{ metric.unit }}</span>
      </div>
    </div>
  </div>
</template>

<script>
const DEFAULT_PALETTE = ['#3cb6d2', '#209bb9', '#8ac7d5', '#1e7a92', '#a3d8e4', '#6bb7c9'];

export default {
  props: {
    data: { type: Array, required: true },
    nameKey: { type: String, default: 'name' },
    title: { type: String, default: '集計' },
    colorMap: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      activeMetric: 'display_count',
      metrics: [
        { key: 'display_count', label: '表示数', unit: '' },
        { key: 'click_count', label: 'クリック数', unit: '' },
        { key: 'ctr', label: 'CTR', unit: '%' },
      ],
      svgSize: 240,
      barWidth: 420,
      barHeight: 280,
      barLabelWidth: 80,
      barBottomPad: 30,
      tooltip: { visible: false, x: 0, y: 0, name: '', value: 0, fill: '' },
    };
  },
  computed: {
    metric() {
      return this.metrics.find(m => m.key === this.activeMetric);
    },
    isRatio() {
      return this.activeMetric === 'ctr';
    },
    totalValue() {
      if (this.isRatio) return null;
      return this.data.reduce((s, d) => s + this.metricValue(d), 0);
    },
    donutSegments() {
      const cx = this.svgSize / 2;
      const cy = this.svgSize / 2;
      const innerR = 70;
      const outerR = 110;
      const total = this.totalValue || 0;
      const gap = 2;

      if (total <= 0) {
        return [];
      }

      const segments = [];
      let startAngle = -90;
      this.data.forEach((d, i) => {
        const value = this.metricValue(d);
        if (value <= 0) return;
        const sweep = (value / total) * 360;
        const endAngle = startAngle + sweep;
        const adjStart = startAngle + gap / 2;
        const adjEnd = endAngle - gap / 2;
        const midAngle = (startAngle + endAngle) / 2;

        const midRad = (midAngle * Math.PI) / 180;
        const labelR = (innerR + outerR) / 2;

        segments.push({
          name: d[this.nameKey],
          value,
          percent: (value / total) * 100,
          path: this.arcPath(cx, cy, innerR, outerR, adjStart, adjEnd),
          fill: this.colorFor(d[this.nameKey], i),
          labelX: cx + labelR * Math.cos(midRad),
          labelY: cy + labelR * Math.sin(midRad),
        });
        startAngle = endAngle;
      });
      return segments;
    },
    barInnerHeight() {
      return this.barHeight - this.barBottomPad;
    },
    barInnerWidth() {
      return this.barWidth - this.barLabelWidth - 60;
    },
    barRangeMax() {
      const maxValue = Math.max(...this.data.map(d => this.metricValue(d)), 1);
      return this.niceCeil(maxValue);
    },
    barItems() {
      const items = this.data.map((d, i) => ({
        name: d[this.nameKey],
        rawValue: this.metricValue(d),
        fill: this.colorFor(d[this.nameKey], i),
      }));
      const rangeMax = this.barRangeMax;
      const innerWidth = this.barInnerWidth;
      const rowHeight = this.barInnerHeight / items.length;
      const barThickness = Math.min(28, rowHeight * 0.55);
      items.forEach((it, idx) => {
        it.width = innerWidth * (it.rawValue / (rangeMax || 1));
        it.height = barThickness;
        it.y = idx * rowHeight + (rowHeight - barThickness) / 2;
        it.value = it.rawValue;
      });
      return items;
    },
    gridLines() {
      const ticks = 5;
      const rangeMax = this.barRangeMax || 1;
      const innerWidth = this.barInnerWidth || 1;
      const lines = [];
      for (let i = 0; i <= ticks; i++) {
        const v = (rangeMax / ticks) * i;
        lines.push({
          x: this.barLabelWidth + (innerWidth * i) / ticks,
          label: `${this.formatValue(v, v < 10 ? 1 : 0)}%`,
        });
      }
      return lines;
    },
  },
  methods: {
    metricValue(d) {
      if (this.activeMetric === 'ctr') {
        const display = d.display_count || 0;
        const click = d.click_count || 0;
        if (display <= 0) return 0;
        return (click * 100) / display;
      }
      return d[this.activeMetric] || 0;
    },
    colorFor(name, index) {
      if (this.colorMap && this.colorMap[name]) return this.colorMap[name];
      return DEFAULT_PALETTE[index % DEFAULT_PALETTE.length];
    },
    legendPercent(d) {
      if (this.isRatio || !this.totalValue) return null;
      const v = this.metricValue(d);
      return ((v / this.totalValue) * 100).toFixed(1);
    },
    formatValue(v, digits) {
      if (v === null || v === undefined || isNaN(v)) return '-';
      return Number(v).toLocaleString('ja-JP', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
    },
    niceCeil(v) {
      if (v <= 0) return 1;
      const exp = Math.floor(Math.log10(v));
      const base = Math.pow(10, exp);
      const n = v / base;
      let nice;
      if (n <= 1) nice = 1;
      else if (n <= 2) nice = 2;
      else if (n <= 5) nice = 5;
      else nice = 10;
      return nice * base;
    },
    arcPath(cx, cy, innerR, outerR, startAngle, endAngle) {
      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;
      const x1 = cx + outerR * Math.cos(startRad);
      const y1 = cy + outerR * Math.sin(startRad);
      const x2 = cx + outerR * Math.cos(endRad);
      const y2 = cy + outerR * Math.sin(endRad);
      const x3 = cx + innerR * Math.cos(endRad);
      const y3 = cy + innerR * Math.sin(endRad);
      const x4 = cx + innerR * Math.cos(startRad);
      const y4 = cy + innerR * Math.sin(startRad);
      const largeArc = endAngle - startAngle > 180 ? 1 : 0;
      return [
        'M', x1, y1,
        'A', outerR, outerR, 0, largeArc, 1, x2, y2,
        'L', x3, y3,
        'A', innerR, innerR, 0, largeArc, 0, x4, y4,
        'Z',
      ].join(' ');
    },
    onHover(event, seg) {
      const container = this.$el.getBoundingClientRect();
      this.tooltip = {
        visible: true,
        x: event.clientX - container.left + 12,
        y: event.clientY - container.top + 12,
        name: seg.name,
        value: seg.value,
        fill: seg.fill,
      };
    },
    onLeave() {
      this.tooltip.visible = false;
    },
  },
};
</script>

<style scoped>
.breakdown-chart {
    position: relative;
    font-family: "Hiragino Kaku Gothic ProN", "Noto Sans JP", Meiryo, sans-serif;
    color: #333;
    background: #fff;
    border: 1px solid #cccccc;
    border-radius: 4px;
    padding: 20px 24px;
    margin-bottom: 16px;
}

.breakdown-chart__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 2px solid #3cb6d2;
    padding-bottom: 10px;
    margin-bottom: 16px;
}

.breakdown-chart__title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #209bb9;
    letter-spacing: 0.02em;
}

.breakdown-chart__metric-tabs {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 20px;
}

.breakdown-chart__pill {
    padding: 6px 18px;
    font-size: 13px;
    font-weight: 700;
    border: 1px solid #cccccc;
    background: #ffffff;
    color: #777777;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.15s;
    font-family: inherit;
    letter-spacing: 0.02em;
}

.breakdown-chart__pill.is-active {
    background: #3cb6d2;
    border-color: #3cb6d2;
    color: #ffffff;
}

.breakdown-chart__main {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 24px;
    align-items: center;
    /* デバイス別/OS別/ブラウザ別のタブ切替時にレイアウトシフトが起きないよう、
       もっとも背が高くなる OS別クリック数ビュー (2層ドーナツ + 内訳) に合わせて固定 */
    min-height: 340px;
}

.breakdown-chart__chart-area {
    width: 100%;
    position: relative;
}

.breakdown-chart__donut-wrap {
    position: relative;
    width: 100%;
    max-width: 320px;
    margin: 0 auto;
}

.breakdown-chart__svg {
    width: 100%;
    height: auto;
    display: block;
}

.breakdown-chart__donut-segment {
    cursor: pointer;
    transition: opacity 0.15s;
}

.breakdown-chart__donut-segment:hover {
    opacity: 0.85;
}

.breakdown-chart__segment-label {
    fill: #ffffff;
    font-size: 12px;
    font-weight: 700;
    pointer-events: none;
}

.breakdown-chart__donut-center {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    pointer-events: none;
}

.breakdown-chart__donut-center-label {
    font-size: 11px;
    color: #777;
    font-weight: 700;
    margin-bottom: 2px;
}

.breakdown-chart__donut-center-value {
    font-size: 24px;
    font-weight: 700;
    color: #209bb9;
    line-height: 1;
}

.breakdown-chart__donut-center-sub {
    font-size: 11px;
    color: #777;
    margin-top: 4px;
}

.breakdown-chart__bar-wrap {
    width: 100%;
}

.breakdown-chart__axis-text {
    fill: #777;
    font-size: 10px;
}

.breakdown-chart__bar-name {
    fill: #333;
    font-size: 12px;
    font-weight: 700;
}

.breakdown-chart__bar-value {
    fill: #333;
    font-size: 11px;
    font-weight: 700;
}

.breakdown-chart__legend-title {
    font-size: 11px;
    color: #777;
    font-weight: 700;
    margin-bottom: 10px;
    letter-spacing: 0.05em;
}

.breakdown-chart__legend-list {
    display: flex;
    flex-direction: column;
    gap: 0;
}

.breakdown-chart__legend-item {
    display: grid;
    grid-template-columns: 16px 1fr auto;
    align-items: center;
    gap: 10px;
    padding: 10px 6px;
    border-bottom: 1px solid #e0edf0;
}

.breakdown-chart__legend-swatch {
    width: 12px;
    height: 12px;
    border-radius: 2px;
}

.breakdown-chart__legend-label {
    font-size: 13px;
    font-weight: 700;
    color: #333;
}

.breakdown-chart__legend-value {
    font-size: 14px;
    font-weight: 700;
    color: #333;
    text-align: right;
    white-space: nowrap;
}

.breakdown-chart__legend-percent {
    font-size: 11px;
    color: #777;
    margin-left: 6px;
    font-weight: 400;
}

.breakdown-chart__tooltip {
    position: absolute;
    background: #ffffff;
    border: 1px solid #3cb6d2;
    border-radius: 4px;
    padding: 8px 14px;
    box-shadow: 0 2px 10px rgba(32, 155, 185, 0.15);
    font-size: 12px;
    color: #333;
    min-width: 140px;
    pointer-events: none;
    z-index: 10;
}

.breakdown-chart__tooltip-name {
    font-weight: 700;
    margin-bottom: 4px;
}

.breakdown-chart__tooltip-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
}

.breakdown-chart__tooltip-value {
    font-weight: 700;
}
</style>
